// ProjectContactsPanel — the "team for this project" panel.
//
// v0.39.0 — initial. Lives on ProjectDetailScreen. Shows the contacts
//           that have been picked into this project; offers an "+ Add"
//           button that opens a modal listing the tenant's master
//           contacts that AREN'T already on this project.
//
// The data model:
//   * Tenant has a master Contacts table (managed at /contacts).
//   * Project has a ProjectContacts join — a subset of the master list.
//   * v0.39.1's defect Assignee dropdown will read from the project's
//     contacts only, so this panel is also the curation step for who
//     can be assigned a defect.
//
// Props:
//   reportId  — required (v0.339.0). The report whose team this is.
//   onChange  — optional. Called with the team as it now stands after a load,
//               an add or a remove (v0.288.0: the register's Team box keeps the
//               item card's Assignee list current with it).
//   bare      — optional. Drops the card frame (the Team box is the frame).
//
// The panel manages its own lists internally and re-fetches when the user
// adds/removes.
//
// v0.288.0 — under 640 px a member is two lines (name + company, then email ·
// phone) instead of six columns, so the panel reads in the register's Team box
// on a phone.
//
// v0.310 — the company's own STAFF on the team (Brett: "add insite staff to the
// team also. By default whoever created the report, then they add other staff
// if required"). Whoever makes a report is on its project's team by default
// (the server does that); "+ Add staff" picks another of the company's active
// sign-ins (read from /api/account-seats, the same list Settings > Seats
// shows) and POSTs { projectId, staffEmail }. A staff member is an ordinary
// team row (a contact typed Staff) so the item card's Assignee list and the
// register's strip show them; Remove works the same. Staff are never emailed
// by Send update. A sign-in list that cannot be read says so — never "no one".
//
// v0.314.0 — a chat PER PROJECT (Brett: "i think ecr might be both a client and
// a subcontractor" … "yes build the per project switch"). Each member who is not
// staff has a Client / Subcontractor choice for THIS project only. It starts at
// the contact's type; a different pick is PATCHed as { chat } and marked "This
// project only"; picking the type's own chat again clears it ({ chat: null }).
// The contact's type and their place on other projects are untouched. Never
// queued offline: the row changes only on the server's answer, and a refusal
// says so and leaves the choice as it was.
//
// v0.316.0 — addTo ('client' | 'trade'), from a group on the register's strip
// (Brett: "can i click here and add a subcontractor or add client directly").
// Once the team is read the contacts list opens by itself, titled for that
// chat, and whoever is picked joins it: when their contact type would put them
// in the other chat (or none), the add carries { chat } so the server sets this
// project's chat in the same write. That add is never queued offline, like the
// chat switch: no connection or a database not ready says nobody was added.
//
// v0.339.0 - THE TEAM OF ONE REPORT (Brett: "Location shows nothing. Do it
// properly."). The panel takes a reportId and reads/writes /api/report-contacts;
// a location has no team of its own any more. Until an owner has run Account >
// Move teams onto reports, the report also shows its location's old team: those
// rows (id 'loc-N', source 'location') are marked "Location team" with no Remove
// and no chat choice - the server refuses both (409). A report whose team is
// short of someone on the location's latest other report (or its old team) is
// offered "Copy the team from <report>" - one POST, people already on it keep
// their own chat. Nothing is copied by itself: a new report starts with whoever
// made it.

import React, { useEffect, useState, useMemo } from 'react';
import { apiGet, apiSend } from '../api';
import { subscribeApiUpdates, urlConcerns } from '../utils/apiFreshness';
import { alertFailure, isSessionEnded, signInEnded } from '../utils/sessionEnd';
import TeamPickerInSpec from './TeamPickerInSpec';
import { importFailure } from './ImportFromInSpecModal';
import { importOutcome } from '../utils/teamPickerInSpec';
import { theme, card, btnPrimary, btnSecondary, input } from '../theme';
import { tradeLabel, tradeColor } from '../utils/trades';
import { chatForKind, projectChat, teamChat } from '../utils/shareTeam.js';

function useNarrow() {
  const [narrow, setNarrow] = useState(() => {
    try { return window.matchMedia('(max-width: 639px)').matches; } catch (_) { return false; }
  });
  useEffect(() => {
    let mq;
    try { mq = window.matchMedia('(max-width: 639px)'); } catch (_) { return undefined; }
    const on = (e) => setNarrow(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', on); else mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', on); else mq.removeListener(on); };
  }, []);
  return narrow;
}

const ADD_TITLES = {
  client: 'Add to the Client chat on this report',
  trade: 'Add to the Subcontractor chat on this report',
};

export default function ProjectContactsPanel({ reportId, addTo = null, onChange, bare = false }) {
  const [projectContacts, setProjectContacts] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picking, setPicking] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState(null);
  const narrow = useNarrow();
  const [loaded, setLoaded] = useState(false);
  const [pickingStaff, setPickingStaff] = useState(false); // v0.310
  // v0.316.0 — the chat the open list adds into (null: by contact type).
  const [pickTo, setPickTo] = useState(null);
  const [autoOpened, setAutoOpened] = useState(false);
  // v0.323.0 - Brett: "could we do a refresh when i go to add another team
  // member so i dont need to get out of the job and back in again", then "I
  // want [it] to be a button otherwise we waste time updating everytime". The
  // list opens at once from what the job read; Refresh list reads Contacts
  // again, past the device's cache, for someone just added or brought in.
  const [refresh, setRefresh] = useState({ state: 'idle' }); // idle | busy | done | failed
  // v0.339.0 - what "Copy the team" would copy: null = nothing to offer,
  // undefined = not read (the offer is simply not shown; the team still is).
  const [copySrc, setCopySrc] = useState(undefined);
  const [copying, setCopying] = useState(false);
  const teamUrl = `/api/report-contacts?reportId=${encodeURIComponent(reportId)}`;
  useEffect(() => {
    if (!addTo || autoOpened || !loaded) return;
    setAutoOpened(true);
    setPickTo(ADD_TITLES[addTo] ? addTo : null);
    setPicking(true);
  }, [addTo, autoOpened, loaded]);

  // Only after the panel has read the team: before that the empty starting list
  // is "not looked yet", and handing it out would clear the caller's own list.
  useEffect(() => {
    if (loaded && onChange) onChange(projectContacts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectContacts, loaded]);

  async function load() {
    setLoading(true); setErr(null);
    try {
      // v0.288.0 — a team that could not be read says so, instead of
      // showing (and handing out) an empty team.
      const [pc, all] = await Promise.all([
        apiGet(teamUrl),
        apiGet('/api/contacts').catch(() => []),
      ]);
      setProjectContacts(Array.isArray(pc) ? pc : []);
      setAllContacts(Array.isArray(all) ? all : []);
      setLoaded(true);
      loadCopySource();
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  async function loadCopySource() {
    try {
      const out = await apiGet(`${teamUrl}&copySource=1`, { fresh: true });
      setCopySrc(out && out.copySource && Number(out.copySource.missing) > 0 ? out.copySource : null);
    } catch (_) {
      setCopySrc(undefined);
    }
  }

  // v0.339.0 - one press copies that team onto this report; never queued.
  async function copyTeam() {
    if (!copySrc || copying) return;
    setCopying(true);
    try {
      const body = copySrc.from === 'location'
        ? { reportId: Number(reportId), copyFrom: 'location' }
        : { reportId: Number(reportId), copyFromReportId: copySrc.reportId };
      const out = await apiSend('POST', '/api/report-contacts', body, { noQueue: true });
      if (!out || !Array.isArray(out.team)) throw new Error('The server did not answer with the team.');
      setProjectContacts(out.team);
      setCopySrc(null);
      loadCopySource();
    } catch (e) {
      alertFailure(e?.offline
        ? 'No connection - nobody was copied. Try again when you are back online.'
        : 'The team was not copied: ' + (e?.message || String(e)), e);
    } finally {
      setCopying(false);
    }
  }

  useEffect(() => { if (reportId) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [reportId]);

  // v0.335.0 - the team is read through the service worker's cache too, so a
  // team changed on another device stayed as it was cached. When the worker
  // says this location's team changed, read it again quietly: what is shown
  // stays until the new answer arrives, and a failed re-read keeps it.
  useEffect(() => {
    if (!reportId) return undefined;
    let alive = true;
    const off = subscribeApiUpdates(async (url) => {
      if (!urlConcerns(url, ['report-contacts'])) return;
      let rid = null;
      let copy = null;
      try {
        const u = new URL(String(url), 'http://x');
        rid = u.searchParams.get('reportId');
        copy = u.searchParams.get('copySource');
      } catch (_) { rid = null; }
      if (rid !== String(reportId) || copy) return;
      try {
        const pc = await apiGet(teamUrl);
        if (alive && Array.isArray(pc)) { setProjectContacts(pc); setLoaded(true); setErr(null); }
      } catch (_) { /* keep the team on screen */ }
    });
    return () => { alive = false; off(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  async function refreshContacts() {
    if (refresh.state === 'busy') return;
    setRefresh({ state: 'busy' });
    try {
      const all = await apiGet('/api/contacts', { fresh: true });
      if (!Array.isArray(all)) throw new Error('no list');
      const before = new Set(allContacts.map((c) => c.id));
      setAllContacts(all);
      setRefresh({ state: 'done', added: all.filter((c) => !before.has(c.id)).length });
    } catch (_) {
      setRefresh({ state: 'failed' });
    }
  }

  const linkedContactIds = useMemo(
    () => new Set(projectContacts.map(c => c.contactId)),
    [projectContacts]
  );
  const available = useMemo(
    () => allContacts.filter(c => !linkedContactIds.has(c.id)),
    [allContacts, linkedContactIds]
  );

  // v0.327.0 — answers null when added, else what went wrong. `quiet` hands
  // that back instead of an alert (the InSpec rows say it on the row); a quiet
  // add is never queued, so "added" is never claimed for something still waiting.
  async function addContact(contact, into = null, { quiet = false } = {}) {
    // v0.316.0 — only when their type would not already put them in that chat.
    const chat = into && contact.kind !== 'staff' && chatForKind(contact.kind) !== into ? into : null;
    setBusyId(contact.id);
    try {
      const link = chat || quiet
        ? await apiSend('POST', '/api/report-contacts', { reportId: Number(reportId), contactId: contact.id, ...(chat ? { chat } : {}) }, { noQueue: true })
        : await apiSend('POST', '/api/report-contacts', {
          reportId: Number(reportId),
          contactId: contact.id,
        });
      if ((chat || quiet) && (!link || link.id == null)) throw new Error('The server did not answer with the team member.');
      // The POST returns the join row + hydrate the embedded contact
      // fields client-side so the list updates without a refetch.
      setProjectContacts(prev => [
        ...prev.filter(x => x.contactId !== contact.id),
        {
          ...link,
          name: contact.name, email: contact.email, phone: contact.phone,
          company: contact.company, trade: contact.trade, notes: contact.notes,
          // v0.302.0 — the register's strip groups by contact type; without it
          // a person just added sat under "No type" until the next read.
          kind: contact.kind ?? null,
          chat: link?.chat ?? chat,
        },
      ].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      return null;
    } catch (e) {
      const text = e?.offline
        ? 'No connection - nobody was added. Try again when you are back online.'
        : 'Add failed: ' + (e?.message || String(e));
      if (quiet) return isSessionEnded(e) || signInEnded() ? { ended: true } : { text };
      alertFailure(text, e);
      return { text };
    } finally {
      setBusyId(null);
    }
  }

  // v0.327.0 — Brett: searching the team picker also searches InSpec; picking
  // one brings them into Contacts and adds them to the team in one press. The
  // import is never queued: it either answered with the contact, or it says why.
  async function addFromInSpec(person) {
    const name = person.name || person.company || 'They';
    let body;
    try {
      body = await apiSend('POST', '/api/import-inspec-contacts', { ids: [person.externalId], returnContacts: true }, { noQueue: true });
    } catch (e) {
      const f = importFailure(e);
      if (f.state === 'ended') return null;
      if (e?.offline) return { tone: 'error', note: 'No connection - nobody was brought in or added, and nothing is waiting to send. Try again when you are back online.' };
      if (f.unconfirmed) return { tone: 'warn', note: 'The server did not confirm the import, so it may have arrived. Press Refresh list - if they are there, pick them above; if not, try again (no one is added twice).' };
      return { tone: 'error', note: f.text || 'The import could not be finished. Nobody was added.' };
    }
    const out = importOutcome(body, name);
    if (!out.contact) return out;
    const c = out.contact;
    setAllContacts((prev) => (prev.some((x) => x.id === c.id)
      ? prev.map((x) => (x.id === c.id ? { ...x, ...c } : x))
      : [...prev, c].sort((a, b) => (a.name || '').localeCompare(b.name || ''))));
    // Linked to a contact already on this team (their email here differs from InSpec's).
    if (linkedContactIds.has(c.id)) return { tone: 'plain', note: `${c.name || name} is already on this team.` };
    const failed = await addContact(c, pickTo, { quiet: true });
    if (!failed || failed.ended) return null;
    return { tone: 'error', note: `${c.name || name} is in Contacts now but was not added to the team: ${failed.text} Pick them in the list above to try again.` };
  }

  // v0.310 — one of the company's own sign-ins joins the team as Staff. The
  // server answers the full team row (it makes or finds the contact).
  async function addStaff(member) {
    const email = String(member.Email || '').trim();
    setBusyId('staff:' + email.toLowerCase());
    try {
      const row = await apiSend('POST', '/api/report-contacts', {
        reportId: Number(reportId),
        staffEmail: email,
      });
      if (row && row.id != null) {
        setProjectContacts(prev => [
          ...prev.filter(x => x.id !== row.id && x.contactId !== row.contactId),
          row,
        ].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      } else {
        await load();
      }
    } catch (e) {
      alertFailure('Add failed: ' + (e?.message || String(e)), e);
    } finally {
      setBusyId(null);
    }
  }

  async function removeContact(pc) {
    if (!window.confirm(`Remove ${pc.name} from this report's team? They stay in your master Contacts list and on other reports.`)) return;
    setBusyId(pc.id);
    try {
      await apiSend('DELETE', `/api/report-contacts/${pc.id}`);
      setProjectContacts(prev => prev.filter(x => x.id !== pc.id));
    } catch (e) {
      alertFailure('Remove failed: ' + (e?.message || String(e)), e);
    } finally {
      setBusyId(null);
    }
  }

  // v0.314.0 — this member's chat on this project. The type's own chat is sent
  // as null, so the row follows the contact's type again.
  async function setChat(pc, value) {
    const chat = value === chatForKind(pc.kind) ? null : value;
    setBusyId(pc.id);
    try {
      const row = await apiSend('PATCH', `/api/report-contacts/${pc.id}`, { chat }, { noQueue: true });
      if (!row || row.id !== pc.id) throw new Error('The server did not answer with the team member.');
      setProjectContacts(prev => prev.map(x => (x.id === pc.id ? { ...x, ...row } : x)));
    } catch (e) {
      alertFailure(e?.offline
        ? 'No connection - the chat was not changed. Try again when you are back online.'
        : 'The chat was not changed: ' + (e?.message || String(e)), e);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div data-part="project-team" style={bare ? {} : { ...card, padding: 14, marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={{ flex: '1 1 220px', minWidth: 0 }}>
          {!bare && (
            <div style={{ fontSize: 11, fontWeight: 800, color: theme.textLight, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Report team
            </div>
          )}
          <div style={{ fontSize: 12, color: theme.textLight, marginTop: bare ? 0 : 2 }}>
            {/* v0.45.20 — Brett: "We don't have companies". The v0.44.0
                rename swapped the UI label Contacts → Companies but
                the unit added to a project is still a person (name +
                email + phone + a Company field). Labels here reverted
                to "team members" so the wording matches reality. */}
            People on this report's team. Pick from your master <a href="/contacts" style={{ color: theme.brand, textDecoration: 'none' }}>contacts list</a>, or add your own staff — whoever made the report is on its team already.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => { setPickTo(null); setRefresh({ state: 'idle' }); setPicking(true); }}
            style={{ ...btnSecondary, padding: '6px 12px', fontSize: 12 }}
            disabled={loading || !loaded}
          >+ Add team member</button>
          <button
            type="button"
            data-part="add-staff"
            onClick={() => setPickingStaff(true)}
            style={{ ...btnSecondary, padding: '6px 12px', fontSize: 12 }}
            disabled={loading || !loaded}
          >+ Add staff</button>
        </div>
      </div>

      {loaded && copySrc && (
        <div data-part="team-copy" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 10px', marginBottom: 8, border: `1px solid ${theme.border}`, borderRadius: 6, fontSize: 12, color: theme.textLight }}>
          <span style={{ flex: '1 1 220px', minWidth: 0 }}>
            {copySrc.from === 'location'
              ? <>{copySrc.missing} {copySrc.missing === 1 ? 'person' : 'people'} from this location's old team {copySrc.missing === 1 ? 'is' : 'are'} not on this report.</>
              : <>{copySrc.missing} {copySrc.missing === 1 ? 'person' : 'people'} on <strong style={{ color: theme.text }}>{copySrc.name || `report ${copySrc.reportId}`}</strong> {copySrc.missing === 1 ? 'is' : 'are'} not on this report.</>}
          </span>
          <button type="button" data-part="team-copy-btn" onClick={copyTeam} disabled={copying}
            style={{ ...btnSecondary, padding: '6px 12px', fontSize: 12 }}>
            {copying ? 'Copying…' : copySrc.from === 'location' ? 'Copy the location team' : `Copy the team from ${copySrc.name || `report ${copySrc.reportId}`}`}
          </button>
        </div>
      )}
      {loaded && projectContacts.some(isLocationRow) && (
        <div data-part="team-location-note" style={{ fontSize: 12, color: theme.textLight, marginBottom: 8 }}>
          Rows marked <strong>Location team</strong> come from this location's old team, which every report there still shows. An owner or admin moves them onto the reports in Account &gt; Move teams onto reports; until then they cannot be removed or changed here.
        </div>
      )}

      {err && (
        <div style={{ padding: 8, background: theme.bg, border: `1px solid ${theme.danger}`, color: theme.danger, borderRadius: 6, marginBottom: 8, fontSize: 12 }}>
          {loaded ? err : <>The team could not be read, so it is not shown: {err}{' '}
            <button type="button" onClick={load} style={{ border: 0, background: 'transparent', padding: 0, color: theme.brand, fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Try again</button></>}
        </div>
      )}

      {loading ? (
        <div style={{ color: theme.textLight, fontSize: 12, padding: '6px 0' }}>Loading…</div>
      ) : !loaded ? null : projectContacts.length === 0 ? (
        <div style={{ fontSize: 13, color: theme.textLight, padding: '8px 0' }}>
          No one on this report's team yet. Click <strong>+ Add team member</strong> to pick from your master contacts list, or <strong>+ Add staff</strong> for one of your own people.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {projectContacts.map(pc => (
            <div key={pc.id} data-part="team-member" style={{
              display: 'grid',
              gridTemplateColumns: narrow ? 'auto minmax(0, 1fr) auto' : 'auto 1.4fr 1.6fr 1.4fr 1.2fr 200px auto',
              gap: 8,
              padding: '6px 8px',
              border: `1px solid ${theme.border}`,
              borderRadius: 6,
              alignItems: 'center',
              fontSize: 13,
              opacity: busyId === pc.id ? 0.5 : 1,
            }}>
              <span style={{
                width: 10, height: 10, borderRadius: '50%',
                background: pc.trade ? tradeColor(pc.trade) : theme.textLight,
                display: 'inline-block',
              }} title={pc.trade ? tradeLabel(pc.trade) : 'No trade'} />
              {narrow ? (
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', overflowWrap: 'anywhere' }}>
                    <span style={{ fontWeight: 600, color: theme.text }}>{pc.name}</span>
                    {pc.kind === 'staff' && <StaffChip />}
                    {isLocationRow(pc) && <LocationChip />}
                    {pc.company && <span style={{ color: theme.textLight }}> · {pc.company}</span>}
                  </span>
                  {(pc.email || pc.phone) && (
                    <span style={{ display: 'block', fontSize: 12, color: theme.textLight, overflowWrap: 'anywhere' }}>
                      {[pc.email, pc.phone].filter(Boolean).join(' · ')}
                    </span>
                  )}
                  {pc.kind !== 'staff' && !isLocationRow(pc) && (
                    <span style={{ display: 'block', marginTop: 4 }}>
                      <ChatPick pc={pc} busy={busyId === pc.id} onPick={setChat} />
                    </span>
                  )}
                </span>
              ) : (
                <>
                  <span style={{ fontWeight: 600, color: theme.text, minWidth: 0 }}>{pc.name}{pc.kind === 'staff' && <StaffChip />}{isLocationRow(pc) && <LocationChip />}</span>
                  <span style={{ color: theme.textLight }}>{pc.email || ''}</span>
                  <span style={{ color: theme.textLight }}>{pc.phone || ''}</span>
                  <span style={{ color: theme.textLight }}>{pc.company || ''}</span>
                  {pc.kind === 'staff' || isLocationRow(pc) ? <span /> : <ChatPick pc={pc} busy={busyId === pc.id} onPick={setChat} />}
                </>
              )}
              {isLocationRow(pc) ? <span /> : <button
                type="button"
                onClick={() => removeContact(pc)}
                disabled={busyId === pc.id}
                style={{
                  border: 0, background: 'transparent', cursor: 'pointer',
                  color: theme.danger, fontSize: 12, fontWeight: 600,
                }}
                title="Remove from this report's team"
              >Remove</button>}
            </div>
          ))}
        </div>
      )}

      {picking && (
        <PickerModal
          contacts={available}
          busyId={busyId}
          title={pickTo ? ADD_TITLES[pickTo] : undefined}
          into={pickTo}
          refresh={refresh}
          onRefresh={refreshContacts}
          onPick={async (c) => { await addContact(c, pickTo); }}
          allContacts={allContacts}
          teamIds={linkedContactIds}
          onAddExisting={async (c) => {
            const failed = await addContact(c, pickTo, { quiet: true });
            return failed && !failed.ended ? { tone: 'error', note: failed.text } : null;
          }}
          onImport={addFromInSpec}
          onClose={() => setPicking(false)}
        />
      )}
      {pickingStaff && (
        <StaffPickerModal
          team={projectContacts}
          busyId={busyId}
          onPick={async (m) => { await addStaff(m); }}
          onClose={() => setPickingStaff(false)}
        />
      )}
    </div>
  );
}

// v0.339.0 - a row the report shows from its location's old team (read-only).
export function isLocationRow(pc) {
  return !!pc && (pc.source === 'location' || String(pc.id).startsWith('loc-'));
}

function LocationChip() {
  return (
    <span data-part="location-chip" title="On the location's old team - moved onto the reports from Account > Move teams onto reports" style={{
      display: 'inline-block', marginLeft: 6, padding: '1px 6px', borderRadius: 999,
      fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
      color: theme.textLight, border: `1px solid ${theme.border}`, verticalAlign: 'middle', lineHeight: 1.4,
    }}>Location team</span>
  );
}

// v0.314.0 — the member's chat on this project: Client or Subcontractor, from
// the contact's type unless picked here. "No type" only while neither applies.
function ChatPick({ pc, busy, onPick }) {
  const value = teamChat(pc) || '';
  const own = !!projectChat(pc);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <select
        data-part="team-chat"
        aria-label={`${pc.name}'s chat on this report`}
        value={value}
        disabled={busy}
        onChange={(e) => { if (e.target.value && e.target.value !== value) onPick(pc, e.target.value); }}
        style={{ ...input, width: 'auto', maxWidth: 160, padding: '3px 6px', fontSize: 12 }}
        title="Which chat this person is in on this report. Their contact type is not changed."
      >
        {!value && <option value="">No type</option>}
        <option value="client">Client</option>
        <option value="trade">Subcontractor</option>
      </select>
      {own && <span data-part="team-chat-own" style={{ fontSize: 11, color: theme.textLight }}>This report only</span>}
    </span>
  );
}

// v0.310 — a small mark on a staff row; the strip groups them under Staff too.
function StaffChip() {
  return (
    <span data-part="staff-chip" style={{
      display: 'inline-block', marginLeft: 6, padding: '1px 6px', borderRadius: 999,
      fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
      color: theme.brand, border: `1px solid ${theme.brand}`, verticalAlign: 'middle', lineHeight: 1.4,
    }}>Staff</span>
  );
}

// v0.310 — pick one of the company's own active sign-ins. Reads
// /api/account-seats (the Seats list) with the caller's own session, so it is
// only ever their own company. Every state is named: reading, could not be
// read (with Try again - never an empty list), everyone already on the team,
// no matches.
function StaffPickerModal({ team, busyId, onPick, onClose }) {
  const [q, setQ] = useState('');
  const [members, setMembers] = useState(null); // null = not read yet
  const [state, setState] = useState('loading'); // loading | ok | failed
  const [err, setErr] = useState(null);

  async function read() {
    setState('loading'); setErr(null);
    try {
      const r = await apiGet('/api/account-seats');
      const list = Array.isArray(r?.members) ? r.members : null;
      if (!list) throw new Error('the sign-in list did not come back');
      setMembers(list.filter(m => m && m.Email && String(m.Status || 'active').toLowerCase() === 'active'));
      setState('ok');
    } catch (e) {
      setErr(e?.message || String(e));
      setState('failed');
    }
  }
  useEffect(() => { read(); }, []);

  const onTeam = useMemo(() => new Set(team.map(t => String(t.email || '').trim().toLowerCase()).filter(Boolean)), [team]);
  const available = useMemo(
    () => (members || []).filter(m => !onTeam.has(String(m.Email).trim().toLowerCase())),
    [members, onTeam]
  );
  const filtered = useMemo(() => {
    if (!q.trim()) return available;
    const needle = q.trim().toLowerCase();
    return available.filter(m =>
      [m.FirstName, m.LastName].filter(Boolean).join(' ').toLowerCase().includes(needle) ||
      String(m.Email).toLowerCase().includes(needle)
    );
  }, [available, q]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopImmediatePropagation();
      e.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  return (
    <div
      data-part="staff-picker"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', // v0.310 — black dims either theme (v0.303 team box)
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 100, padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: theme.surface, width: 560, maxWidth: '100%', maxHeight: '80vh',
          borderRadius: 10, padding: 18,
          border: '1px solid var(--border-strong)', boxShadow: 'var(--shadow-3)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>Add one of your staff to this report's team</h3>
          <button
            type="button"
            onClick={onClose}
            style={{ border: 0, background: 'transparent', cursor: 'pointer', fontSize: 18, color: theme.textLight, lineHeight: 1 }}
          >×</button>
        </div>
        <div style={{ fontSize: 12, color: theme.textLight, marginBottom: 10 }}>
          Your company's sign-ins. Staff on the team can be assigned items; they are not emailed by Send update.
        </div>
        <input
          autoFocus
          placeholder="Search by name or email…"
          value={q}
          onChange={e => setQ(e.target.value)}
          style={{ ...input, marginBottom: 10 }}
          disabled={state !== 'ok'}
        />
        {state === 'loading' ? (
          <div data-state="staff-loading" style={{ fontSize: 13, color: theme.textLight, padding: '8px 0' }}>Reading your company's sign-ins…</div>
        ) : state === 'failed' ? (
          <div data-state="staff-failed" style={{ padding: 8, background: theme.bg, border: `1px solid ${theme.danger}`, color: theme.danger, borderRadius: 6, fontSize: 12 }}>
            Your company's sign-ins could not be read, so none are listed: {err}{' '}
            <button type="button" onClick={read} style={{ border: 0, background: 'transparent', padding: 0, color: theme.brand, fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Try again</button>
          </div>
        ) : available.length === 0 ? (
          <div data-state="staff-all-on" style={{ fontSize: 13, color: theme.textLight, padding: '8px 0' }}>
            {members.length === 0 ? 'Your company has no active sign-ins to add.' : 'Everyone with a sign-in is already on this report\'s team.'}
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ fontSize: 13, color: theme.textLight, padding: '8px 0' }}>No matches. Try a shorter search.</div>
        ) : (
          <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {filtered.map(m => {
              const email = String(m.Email).trim();
              const key = 'staff:' + email.toLowerCase();
              const name = [m.FirstName, m.LastName].filter(Boolean).join(' ') || email;
              return (
                <button
                  key={m.UserId || email}
                  type="button"
                  onClick={() => onPick(m)}
                  disabled={busyId === key}
                  style={{
                    display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'center',
                    padding: '8px 10px', border: `1px solid ${theme.border}`, borderRadius: 6,
                    background: theme.surface, cursor: busyId === key ? 'default' : 'pointer',
                    textAlign: 'left', fontFamily: 'inherit', opacity: busyId === key ? 0.5 : 1,
                  }}
                >
                  <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: theme.text }}>{name}</span>
                    <span style={{ fontSize: 11, color: theme.textLight, marginLeft: 8 }}>{[email, m.Role].filter(Boolean).join(' · ')}</span>
                  </span>
                  <span style={{ fontSize: 11, color: theme.brand, fontWeight: 700 }}>+ Add</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function RefreshNote({ refresh }) {
  if (refresh.state === 'failed') {
    return <div data-state="refresh-failed" style={{ fontSize: 12, color: theme.danger, marginBottom: 8 }}>The list could not be read again — this is what the job read when it opened.</div>;
  }
  if (refresh.state === 'done') {
    const n = refresh.added || 0;
    return (
      <div data-state="refresh-done" style={{ fontSize: 12, color: theme.textLight, marginBottom: 8 }}>
        {n ? `List refreshed — ${n === 1 ? '1 new contact' : `${n} new contacts`}.` : 'List refreshed — no one new in Contacts.'}
      </div>
    );
  }
  return null;
}

function PickerModal({ contacts, busyId, title = 'Add a team member to this report', into = null, refresh = { state: 'idle' }, onRefresh, onPick, allContacts = [], teamIds = null, onAddExisting, onImport, onClose }) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    if (!q.trim()) return contacts;
    const needle = q.trim().toLowerCase();
    return contacts.filter(c =>
      (c.name || '').toLowerCase().includes(needle) ||
      (c.email || '').toLowerCase().includes(needle) ||
      (c.company || '').toLowerCase().includes(needle)
    );
  }, [contacts, q]);

  // v0.288.0 — Esc closes the picker only. Caught on the way DOWN so the Team
  // box it may sit in (which closes on Esc too) never sees it.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopImmediatePropagation();
      e.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  return (
    <div
      data-part="team-picker"
      data-into={into || undefined}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', // v0.310 — black dims either theme (v0.303 team box)
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 100, padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: theme.surface, width: 560, maxWidth: '100%', maxHeight: '80vh',
          borderRadius: 10, padding: 18,
          border: '1px solid var(--border-strong)', boxShadow: 'var(--shadow-3)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>{title}</h3>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          {onRefresh && (
            <button
              type="button"
              data-part="refresh-contacts"
              onClick={onRefresh}
              disabled={refresh.state === 'busy'}
              title="Read Contacts again — for someone just added or brought in from InSpec"
              style={{ ...btnSecondary, padding: '5px 10px', fontSize: 12, opacity: refresh.state === 'busy' ? 0.6 : 1 }}
            >{refresh.state === 'busy' ? 'Refreshing…' : '↻ Refresh list'}</button>
          )}
          <button
            type="button"
            onClick={onClose}
            style={{
              border: 0, background: 'transparent', cursor: 'pointer',
              fontSize: 18, color: theme.textLight, lineHeight: 1,
            }}
          >×</button>
          </span>
        </div>
        <RefreshNote refresh={refresh} />
        <input
          autoFocus
          placeholder={onImport ? 'Search your contacts and InSpec by name, email or company…' : 'Search by name, email or company…'}
          value={q}
          onChange={e => setQ(e.target.value)}
          style={{ ...input, marginBottom: 10 }}
        />
        {/* v0.327.0 — one scroll area: your contacts first, then From InSpec
            (it shows from two letters, under the DropPin matches or the
            "no matches" line). */}
        <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {contacts.length === 0 ? (
          <div style={{ fontSize: 13, color: theme.textLight, padding: '8px 0' }}>
            Everyone in your master contacts list is already on this report's team. Add new
            people at <a href="/contacts" style={{ color: theme.brand }}>Contacts</a>{onImport ? ', or search above to find them in InSpec' : ''}.
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ fontSize: 13, color: theme.textLight, padding: '8px 0' }}>
            No matches in your contacts. Try a shorter search.
          </div>
        ) : (
          <>
            {filtered.map(c => (
              <button
                key={c.id}
                type="button"
                onClick={() => onPick(c)}
                disabled={busyId === c.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr auto',
                  gap: 10,
                  alignItems: 'center',
                  padding: '8px 10px',
                  border: `1px solid ${theme.border}`,
                  borderRadius: 6,
                  background: theme.surface,
                  cursor: busyId === c.id ? 'default' : 'pointer',
                  textAlign: 'left',
                  fontFamily: 'inherit',
                  opacity: busyId === c.id ? 0.5 : 1,
                }}
              >
                <span style={{
                  width: 10, height: 10, borderRadius: '50%',
                  background: c.trade ? tradeColor(c.trade) : theme.textLight,
                  display: 'inline-block',
                }} />
                <span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: theme.text }}>{c.name}</span>
                  <span style={{ fontSize: 11, color: theme.textLight, marginLeft: 8 }}>
                    {[c.company, c.email].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <span style={{ fontSize: 11, color: theme.brand, fontWeight: 700 }}>+ Add</span>
              </button>
            ))}
          </>
        )}
        {onImport && (
          <TeamPickerInSpec q={q} contacts={allContacts} teamIds={teamIds} onAddExisting={onAddExisting} onImport={onImport} />
        )}
        </div>
      </div>
    </div>
  );
}
